import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Boxes, Calculator, Receipt, Upload, ArrowUpRight, BookOpen, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-border rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" strokeWidth={2} />
    </div>
    <div className="num text-4xl font-bold mt-4 tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div className="fade-up">
      <PageHeader title="Přehled" subtitle="Souhrn kalkulací a nakupovaných surovin">
        <Button data-testid="dashboard-new-calc" onClick={() => nav("/kalkulace")}
          className="rounded-full">
          <Calculator className="h-4 w-4 mr-2" /> Kalkulace
        </Button>
      </PageHeader>

      <div className="px-10 py-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={Calculator} label="Kalkulace" value={stats ? stats.calculations : "—"} testid="stat-calculations" />
          <StatCard icon={BookOpen} label="Receptury" value={stats ? stats.recipes : "—"} testid="stat-recipes" />
          <StatCard icon={Building2} label="Odběratelé" value={stats ? fmt(stats.customers, 0) : "—"} testid="stat-customers" />
          <StatCard icon={Boxes} label="Surovin v číselníku" value={stats ? stats.materials : "—"} testid="stat-materials" />
          <StatCard icon={Receipt} label="Řádků nákupu" value={stats ? fmt(stats.total_purchases, 0) : "—"} testid="stat-purchases" />
          <StatCard icon={Upload} label="Importů" value={stats ? stats.imports : "—"} testid="stat-imports" />
        </div>

        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-display text-lg font-medium">Poslední kalkulace</h3>
            <button className="text-sm text-primary hover:underline flex items-center gap-1"
              onClick={() => nav("/kalkulace")} data-testid="dashboard-view-all">
              Zobrazit vše <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {stats && stats.recent_calculations.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground text-sm">
              Zatím žádné kalkulace. Nejprve naimportujte data a vytvořte kalkulaci.
            </div>
          )}
          {stats && stats.recent_calculations.map((c) => (
            <div key={c.id}
              data-testid={`dashboard-calc-${c.id}`}
              onClick={() => nav(`/kalkulace/${c.id}`)}
              className="px-6 py-4 border-b border-border last:border-0 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors">
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {c.author} · změna {fmtDate(c.updated_at)} · {c.items?.length || 0} položek
                </div>
              </div>
              <div className="num text-right">
                <div className="text-[10px] uppercase tracking-wider text-primary/80 font-semibold">Cena/ks</div>
                <div className="text-lg font-bold text-primary">{fmt((c.total || 0) / (c.portions || 1))} Kč</div>
                <div className="text-xs text-muted-foreground">dávka {fmt(c.total)} Kč · {fmt(c.portions || 1, 0)} ks</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
